<template>
  <div class="index-page-size" :class="{ 'index-page-size--dense': dense }">
    <span v-if="label" class="index-page-size__label">{{ label }}</span>
    <v-select
      :model-value="modelValue"
      :items="items"
      density="compact"
      hide-details
      :variant="variant"
      menu-icon="mdi-chevron-down"
      class="index-page-size__select"
      :style="{ width: `${resolvedWidth}px` }"
      @update:model-value="emit('update:modelValue', $event)"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: { type: [Number, String], default: 25 },
  items: {
    type: Array,
    // Match classic DataTables lengthMenu: 10/25/50/100/All (-1 = show all).
    default: () => [
      { title: '10', value: 10 },
      { title: '25', value: 25 },
      { title: '50', value: 50 },
      { title: '100', value: 100 },
      { title: 'All', value: -1 },
    ],
  },
  /** External label shown beside the select (empty string hides it). */
  label: { type: String, default: 'Rows' },
  variant: { type: String, default: 'outlined' },
  /** Select control width in px (number only — label sits outside). */
  selectWidth: { type: Number, default: undefined },
  dense: { type: Boolean, default: false },
  /** @deprecated Prefer selectWidth; kept so existing callers keep working. */
  maxWidth: { type: Number, default: undefined },
  /** @deprecated Prefer selectWidth; kept so existing callers keep working. */
  minWidth: { type: Number, default: undefined },
})

const emit = defineEmits(['update:modelValue'])

const resolvedWidth = computed(() => props.selectWidth ?? props.maxWidth ?? 80)
</script>

<style scoped>
.index-page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  white-space: nowrap;
}

.index-page-size__label {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1;
}

.index-page-size__select {
  flex: 0 0 auto;
}

.index-page-size__select :deep(.v-field) {
  min-height: 32px !important;
  --v-input-control-height: 32px;
}

.index-page-size__select :deep(.v-field__input) {
  min-height: 32px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-inline: 10px 4px;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.index-page-size__select :deep(.v-field__append-inner) {
  padding-inline-start: 0;
  padding-top: 0 !important;
  align-self: center;
}

.index-page-size__select :deep(.v-select__selection-text) {
  overflow: visible;
}

.index-page-size__select :deep(.v-field__outline) {
  --v-field-border-opacity: 0.22;
}

.index-page-size--dense .index-page-size__label {
  font-size: 0.75rem;
}

.index-page-size--dense .index-page-size__select :deep(.v-field) {
  min-height: 28px !important;
  --v-input-control-height: 28px;
}

.index-page-size--dense .index-page-size__select :deep(.v-field__input) {
  min-height: 28px !important;
  font-size: 0.8125rem;
}
</style>
